import {useEffect,useRef,useState} from "react";
import {useSelector} from "react-redux";
import {API_URL} from "./environment";

export function aiHref(route){
  const configured=process.env.REACT_APP_AI_WORKSPACE_URL;
  if(!configured)return null;
  const url=new URL(configured,window.location.origin);
  if(url.origin!==window.location.origin||url.username||url.password||url.search)return null;
  url.hash="/"+route;return url.href;
}
// R33 (I1-028): fields typed into since the last submit, while still on the page, are unsaved input.
// React keeps the value attribute in step with controlled inputs, so edits are tracked by event.
const edited=new Set();
let tracking=false;
function track(){
  if(tracking||typeof document==="undefined")return;tracking=true;
  document.addEventListener("input",event=>{if(event.target?.matches?.("input,textarea,select,[contenteditable]")){hasUnsavedInput();edited.add(event.target);}},true);
  document.addEventListener("submit",event=>{for(const field of [...edited])if(event.target.contains(field))edited.delete(field);},true);
}
export function hasUnsavedInput(){for(const field of [...edited]){if(field.isConnected)return true;edited.delete(field);}return false;}
// A mode change reloads at once on a clean page; with unsaved input the user is asked first, and a
// declined reload waits until the page has no unsaved input. Shared by every mounted instance, so
// the user is asked once per change. R34: while a declined reload waits, the page already runs in
// the new mode (features follow it, reload_pending is set); only the full reload waits.
let declinedMode=null;
function reloadForMode(){
  if(hasUnsavedInput()&&!window.confirm("The assistant was updated and this page needs to reload. Reload now? Unsaved changes on this page will be lost."))return false;
  window.location.reload();return true;
}
// The last answer for the signed-in user is remembered for this browser tab, so the AI entries appear at once
// when a page opens instead of after the round trip; every poll still re-checks and replaces it. It only
// controls what is visible (Stratic AI checks access on every request), is bound to this sign-in by a
// digest of the token, and is cleared on Logout (clearAiFeaturesCache).
const CACHE_KEY="stratic-ai-features";
let memoryCache=null;
function tokenDigest(token){let h=5381;for(let i=0;i<token.length;i++)h=((h<<5)+h+token.charCodeAt(i))|0;return String(h>>>0)+":"+token.length;}
function cached(token){
  if(!token)return null;const owner=tokenDigest(token);
  if(memoryCache?.owner===owner)return memoryCache.data;
  try{const stored=JSON.parse(sessionStorage.getItem(CACHE_KEY)||"null");if(stored?.owner===owner&&stored.data){memoryCache=stored;return stored.data;}}catch(_){}
  return null;
}
function remember(token,data){
  const entry={owner:tokenDigest(token),data:{enabled:data.enabled,agent_workspace:data.agent_workspace,bot_mode:data.bot_mode}};
  memoryCache=entry;try{sessionStorage.setItem(CACHE_KEY,JSON.stringify(entry));}catch(_){}
}
export function clearAiFeaturesCache(){memoryCache=null;try{sessionStorage.removeItem(CACHE_KEY);}catch(_){}}
export default function useAiFeatures(){
  track();
  const token=useSelector(state=>state.auth.accessToken);
  const [state,setState]=useState(()=>{const known=cached(token);return known?{...known,status:"ready",cached:true}:{status:"loading",enabled:false,agent_workspace:false,bot_mode:"off"};});
  const priorMode=useRef(null),pendingMode=useRef(null);
  useEffect(()=>{
    let active=true,inflight=null;
    const load=async()=>{
      if(!token){setState({status:"ready",enabled:false,agent_workspace:false,bot_mode:"old_bot"});return;}
      if(inflight)return;
      const controller=new AbortController();inflight=controller;
      const timeout=setTimeout(()=>controller.abort(),7000);
      try{
        const response=await fetch(API_URL+"/ai/features",{headers:{Authorization:`Bearer ${token}`},cache:"no-store",signal:controller.signal});
        if(!response.ok)throw new Error("Feature access unavailable");
        const data=await response.json();
        if(!["stratic","old_bot","off"].includes(data.bot_mode)||typeof data.enabled!=="boolean")throw new Error("Invalid assistant mode");
        if(!active)return;
        // Reload on a bot-mode transition: third-party script side effects cannot
        // be reliably undone by removing only its script node. The new mode must be seen on
        // two consecutive polls, so one flapping response never discards unsaved form state.
        if(priorMode.current!==null&&priorMode.current!==data.bot_mode){
          if(declinedMode===data.bot_mode){if(!hasUnsavedInput())window.location.reload();else setState({...data,status:"ready",reload_pending:true});return;}
          if(pendingMode.current===data.bot_mode){if(!reloadForMode()){declinedMode=data.bot_mode;setState({...data,status:"ready",reload_pending:true});}return;}
          pendingMode.current=data.bot_mode;return;
        }
        if(declinedMode!==null&&declinedMode!==data.bot_mode)declinedMode=null;
        pendingMode.current=null;priorMode.current=data.bot_mode;
        remember(token,data);
        setState({...data,status:"ready"});
      // R35 (scan 6): a failed poll keeps the mode the page was loaded in (the old bot's script is only
      // ever started by a reload); features are off until a poll succeeds. Never polled: the old bot.
      }catch(_){if(active){const mode=priorMode.current??"old_bot";setState({status:"ready",enabled:false,agent_workspace:false,bot_mode:mode,degraded:true});}}
      finally{clearTimeout(timeout);inflight=null;}
    };
    void load();const timer=setInterval(()=>void load(),15000);
    const focus=()=>void load();window.addEventListener("focus",focus);
    return()=>{active=false;inflight?.abort();clearInterval(timer);window.removeEventListener("focus",focus);};
  },[token]);
  return state;
}
